import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import InstallApp from '../components/InstallApp';
import { 
  FileText, 
  Image, 
  Volume2, 
  Sparkles, 
  ArrowRight,
  Zap,
  Globe,
  Layers,
  Hash
} from 'lucide-react';

const brands = [
  { name: 'Saketh.ai', color: '#8B5CF6', tagline: 'AI Voice Agents' },
  { name: 'Saketh Academy', color: '#10B981', tagline: 'Future Tech Learning' },
  { name: 'MarketMind Pro', color: '#EF4444', tagline: 'AI Trading Signals' },
  { name: 'AILaw.me', color: '#3B82F6', tagline: 'Legal Automation' },
  { name: 'OptimoAI.io', color: '#6366F1', tagline: 'Legal Workflows' },
];

const features = [
  {
    icon: FileText,
    title: 'AI Script Generator',
    description: 'Create engaging scripts for YouTube videos, shorts, reels, and Instagram posts.',
    color: '#8B5CF6'
  },
  {
    icon: Image,
    title: 'AI Image Creator',
    description: 'Generate stunning thumbnails and social media visuals with GPT Image.',
    color: '#10B981'
  },
  {
    icon: Volume2,
    title: 'Voice Narration',
    description: 'Convert scripts to professional voice-overs with ElevenLabs.',
    color: '#3B82F6'
  },
  {
    icon: Hash,
    title: 'AI Hashtag Generator',
    description: 'Auto-generate trending hashtags optimized for YouTube, Instagram & TikTok.',
    color: '#F59E0B'
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#09090b] overflow-hidden">
      {/* Hero Section */}
      <header className="relative">
        {/* Gradient Background */}
        <div className="absolute inset-0 overflow-hidden">
          <div 
            className="absolute top-0 left-1/4 w-[600px] h-[600px] opacity-30 blur-3xl"
            style={{ background: 'radial-gradient(circle, #8B5CF6 0%, transparent 70%)' }}
          />
          <div 
            className="absolute top-40 right-1/4 w-[400px] h-[400px] opacity-20 blur-3xl"
            style={{ background: 'radial-gradient(circle, #10B981 0%, transparent 70%)' }}
          />
        </div>

        {/* Navigation */}
        <nav className="relative z-10 max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white font-['Manrope']">studio.saketh.io</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="hidden sm:block">
              <InstallApp variant="icon" />
            </div>
            <Link to="/login">
              <Button variant="ghost" className="text-zinc-300 hover:text-white hover:bg-zinc-800/50" data-testid="nav-login-btn">
                Sign In
              </Button>
            </Link>
            <Link to="/register">
              <Button 
                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-full px-6"
                data-testid="nav-register-btn"
              >
                Get Started
              </Button>
            </Link>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-20 pb-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/20 mb-8">
              <Zap className="w-4 h-4 text-violet-400" />
              <span className="text-sm text-violet-300">Powered by GPT-5.2 & ElevenLabs</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight mb-6 font-['Manrope']">
              Create Content for
              <br />
              <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                All Your Brands
              </span>
            </h1>
            
            <p className="text-xl text-zinc-400 max-w-2xl mb-10 leading-relaxed">
              Generate professional scripts, images, and voice-overs for YouTube videos, 
              shorts, reels, and Instagram posts. One studio for all your websites.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-full px-8 h-14 text-lg font-medium transition-transform hover:-translate-y-0.5"
                  data-testid="hero-cta-btn"
                >
                  Start Creating Free
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <InstallApp variant="landing" />
            </div>
          </motion.div>

          {/* Brand Pills */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mt-16 flex flex-wrap gap-3"
          >
            {brands.map((brand, i) => (
              <div
                key={brand.name}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-900/50 border border-zinc-800 hover:border-zinc-700 transition-colors"
              >
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ background: brand.color }}
                />
                <span className="text-sm text-zinc-300">{brand.name}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </header>

      {/* Features Section */}
      <section className="relative py-24 bg-zinc-950/50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-white mb-4 font-['Manrope']">
              Everything You Need
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Create consistent, professional content across all your brands with AI-powered tools.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="group p-8 rounded-2xl bg-zinc-900/50 backdrop-blur-xl border border-zinc-800 hover:border-zinc-700 transition-all duration-300"
              >
                <div 
                  className="w-14 h-14 rounded-xl flex items-center justify-center mb-6"
                  style={{ background: `${feature.color}20` }}
                >
                  <feature.icon className="w-7 h-7" style={{ color: feature.color }} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3 font-['Manrope']">
                  {feature.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Types Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold text-white mb-6 font-['Manrope']">
                Create All Content Types
              </h2>
              <p className="text-zinc-400 text-lg mb-8 leading-relaxed">
                From quick 60-second shorts to detailed 10-minute YouTube videos, 
                generate scripts tailored for each platform and audience.
              </p>
              
              <div className="space-y-4">
                {[
                  { label: 'YouTube Shorts', duration: '60 seconds', icon: Globe },
                  { label: 'Instagram Reels', duration: '60 seconds', icon: Layers },
                  { label: 'YouTube Videos', duration: '8-10 minutes', icon: FileText },
                  { label: 'Instagram Posts', duration: 'Captions', icon: Image },
                ].map((type, i) => (
                  <div 
                    key={type.label}
                    className="flex items-center gap-4 p-4 rounded-xl bg-zinc-900/30 border border-zinc-800"
                  >
                    <type.icon className="w-5 h-5 text-violet-400" />
                    <div className="flex-1">
                      <span className="text-white font-medium">{type.label}</span>
                    </div>
                    <span className="text-sm text-zinc-500">{type.duration}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-violet-500/20 to-transparent rounded-3xl blur-2xl" />
              <div className="relative bg-zinc-900/80 backdrop-blur-xl rounded-2xl border border-zinc-800 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>
                <div className="space-y-3">
                  <div className="h-4 bg-zinc-800 rounded w-3/4" />
                  <div className="h-4 bg-zinc-800 rounded w-full" />
                  <div className="h-4 bg-zinc-800 rounded w-5/6" />
                  <div className="h-4 bg-violet-500/20 rounded w-2/3 mt-6" />
                  <div className="h-4 bg-violet-500/20 rounded w-4/5" />
                  <div className="h-4 bg-violet-500/20 rounded w-3/4" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative">
        <div 
          className="absolute inset-0 opacity-30"
          style={{ 
            background: 'radial-gradient(ellipse at center, #8B5CF6 0%, transparent 60%)'
          }}
        />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Manrope']">
              Ready to Scale Your Content?
            </h2>
            <p className="text-xl text-zinc-400 mb-10">
              Start creating professional content for all your brands today.
            </p>
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-full px-10 h-14 text-lg font-medium transition-transform hover:-translate-y-0.5"
              >
                Get Started Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-400" />
            <span className="text-zinc-400">studio.saketh.io</span>
          </div>
          <p className="text-zinc-500 text-sm">
            © 2026 Saketh.io. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
